import React, { useState, useEffect } from 'react';
import {
  SearchOutlined,
  DownloadOutlined,
  PrinterOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FileTextOutlined,
  CarOutlined,
  UserOutlined,
  CalendarOutlined,
  SafetyOutlined,
  EnvironmentOutlined,
  WarningOutlined,
  ReloadOutlined,
  DownOutlined,
  UpOutlined
} from '@ant-design/icons';
import {
  Table,
  Input,
  Button,
  Space,
  Card,
  Typography,
  Tag,
  Tooltip,
  message,
  Modal,
  Badge,
  Row,
  Col,
  Select,
  Statistic,
  Alert,
  Grid
} from 'antd';
import { Pagination } from 'antd';
import * as XLSX from 'xlsx';
import { getVehicleInsurances, deleteVehicleInsurance } from '../Services/Services';
import logo from '../Logo.png';
import html2pdf from 'html2pdf.js';

const { Title, Text } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

const VehicleReport = ({ onEdit }) => {
  const screens = useBreakpoint();
  const isMobile = !screens.md; // <768px

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [filters, setFilters] = useState({
    vehicleNo: '',
    ownerName: '',
    expiryFilter: ''
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [expiryStats, setExpiryStats] = useState({
    taxExpiring: 0,
    fitnessExpiring: 0,
    insuranceExpiring: 0,
    pollutionExpiring: 0
  });
  const [expandedKeys, setExpandedKeys] = useState([]); // for mobile card expand

  const BASE_URL = "http://127.0.0.1:8000";

  const isExpiringSoon = (dateString) => {
    if (!dateString || dateString === '—' || dateString === 'N/A') return false;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const expiryDate = new Date(dateString);
    const diffDays = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 30;
  };

  const isExpired = (dateString) => {
    if (!dateString || dateString === '—' || dateString === 'N/A') return false;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const expiryDate = new Date(dateString);
    return expiryDate < today;
  };

  const getStatusColor = (dateString) => {
    if (!dateString || dateString === '—' || dateString === 'N/A') return 'default';
    if (isExpired(dateString)) return 'red';
    if (isExpiringSoon(dateString)) return 'orange';
    return 'green';
  };

  const getStatusText = (dateString) => {
    if (!dateString || dateString === '—' || dateString === 'N/A') return 'N/A';
    if (isExpired(dateString)) return 'Expired';
    if (isExpiringSoon(dateString)) return 'Expiring Soon';
    return 'Valid';
  };

  const handleViewDoc = (docPath) => {
    if (!docPath) { message.error("Document not found!"); return; }
    const fullUrl = docPath.startsWith('http') ? docPath : `${BASE_URL}${docPath}`;
    window.open(fullUrl, '_blank');
  };

  const handleDownload = async (path, fileName) => {
    if (!path) return;
    const fileUrl = path.startsWith('http') ? path : `${BASE_URL}${path}`;
    try {
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error("Download failed");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || "document.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('Download started');
    } catch (error) {
      window.open(fileUrl, '_blank');
    }
  };

  const handleSinglePrint = (item) => {
    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    const formattedTime = currentDate.toLocaleTimeString('en-IN');

    const getStatusBadge = (date) => {
      if (!date || date === '—' || date === 'N/A') return '<span class="status-na">Not Available</span>';
      const expired = isExpired(date);
      const expiringSoon = isExpiringSoon(date);
      if (expired) return '<span class="status-expired">⚠️ Expired</span>';
      if (expiringSoon) return '<span class="status-warning">⚠️ Expiring Soon</span>';
      return '<span class="status-valid">✓ Valid</span>';
    };

    const logoSrc = logo;

    const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Vehicle Document Report - ${item.vehicleNo}</title>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        html, body {
          background: white;
          font-family: 'Times New Roman', Times, serif;
        }
        body {
          padding: 10px;
        }
        .print-master-wrapper {
          width: 100%;
          max-width: 850px;
          margin: 0 auto;
          background: #ffffff;
          border: 1px solid #a3a3a3;
          padding: 15px;
          border-radius: 4px;
          position: relative;
        }
        @media print {
          body {
            padding: 0;
          }
          .print-master-wrapper {
            border: none;
            padding: 0;
          }
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
        .top-accent-bar {
          margin: -15px -15px 15px -15px;
          height: 5px;
          background: linear-gradient(90deg, #103f7a 75%, #1c7c34 75%);
          border-radius: 4px 4px 0 0;
        }
        .lh-header-main {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 20px;
          padding: 5px 10px 15px 10px;
        }
        .lh-logo-container {
          width: 85px;
          min-width: 85px;
        }
        .lh-logo-img {
          width: 100%;
          height: auto;
          object-fit: contain;
        }
        .lh-brand-section {
          flex: 1;
          text-align: center;
        }
        .lh-main-title {
          font-size: 24px;
          font-weight: bold;
          color: #103f7a;
          letter-spacing: 0.5px;
          line-height: 1.2;
        }
        .lh-sub-address {
          font-size: 10.5px;
          color: #334155;
          margin-top: 5px;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }
        .lh-italic-tag {
          font-size: 10px;
          color: #1c7c34;
          font-style: italic;
          margin-top: 3px;
        }
        .lh-contacts {
          text-align: right;
          border-left: 1px solid #cbd5e1;
          padding-left: 15px;
          min-width: 150px;
          font-size: 11px;
          color: #334155;
        }
        .lh-owner-name {
          font-size: 12px;
          font-weight: bold;
          color: #103f7a;
          margin-bottom: 3px;
        }
        .lh-headline-label {
          text-align: center;
          background: #103f7a;
          color: #ffffff;
          font-size: 13px;
          font-weight: bold;
          padding: 4px 0;
          margin: 5px 10px 15px 10px;
          letter-spacing: 3px;
          border-radius: 2px;
        }
        .bill-info-bar {
          margin: 0 10px 12px 10px;
          border: 1px solid #cbd5e1;
          border-radius: 3px;
          background: #fdfdfd;
          padding: 8px 12px;
          display: flex;
          justify-content: space-between;
          flex-wrap: wrap;
          font-size: 11px;
        }
        .info-group {
          display: flex;
          gap: 20px;
        }
        .info-label {
          font-weight: bold;
          color: #525252;
        }
        .info-value {
          color: #000000;
          margin-left: 4px;
        }
        .details-grid {
          margin: 0 10px 15px 10px;
          padding: 12px;
          background: #ffffff;
          border: 1px solid #cbd5e1;
          border-radius: 3px;
        }
        .grid-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 11.5px;
        }
        .grid-label {
          font-weight: bold;
          color: #103f7a;
        }
        .grid-value {
          color: #000000;
          margin-left: 6px;
        }
        .content-area-cell {
          padding: 5px 10px 15px 10px;
          position: relative;
        }
        .watermark-wrapper {
          position: absolute;
          top: 40%;
          left: 50%;
          transform: translate(-50%, -50%) rotate(-12deg);
          width: 30%;
          opacity: 0.03;
          pointer-events: none;
          z-index: 0;
        }
        .items-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 11.5px;
          border: 1px solid #a3a3a3;
          margin-bottom: 15px;
        }
        .items-table th {
          background: #103f7a;
          color: #ffffff;
          font-weight: bold;
          padding: 8px;
          border-right: 1px solid #cbd5e1;
          font-size: 11px;
          text-align: left;
        }
        .items-table th.center { text-align: center; }
        .items-table td {
          border-bottom: 1px solid #cbd5e1;
          padding: 10px 8px;
          color: #1f2937;
        }
        .items-table td.center { text-align: center; }
        .items-table tr:last-child td { border-bottom: none; }
        
        .status-valid, .status-expired, .status-warning, .status-na {
          font-size: 11px;
          font-weight: bold;
        }
        .status-valid { color: #166534; }
        .status-expired { color: #991b1b; font-weight: bold; }
        .status-warning { color: #9a3412; }
        .status-na { color: #4b5563; }

        .bank-details {
          margin: 15px 0 20px 0;
          padding: 10px 12px;
          background: #f7faff;
          border-left: 4px solid #103f7a;
          border-radius: 3px;
          font-size: 11px;
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          gap: 15px;
          border-top: 1px solid #e0f2fe;
          border-right: 1px solid #e0f2fe;
          border-bottom: 1px solid #e0f2fe;
        }
        .bank-details strong { color: #103f7a; }
        .bank-details span { font-weight: bold; }

        .signature-section {
          display: flex;
          justify-content: space-between;
          margin: 35px 15px 10px 15px;
          font-size: 11px;
        }
        .signature-line {
          text-align: center;
          width: 30%;
        }
        .signature-box {
          border-bottom: 1px solid #cbd5e1;
          height: 35px;
          margin-bottom: 5px;
        }
        .signature-line .auth-title {
          font-weight: bold;
          color: #103f7a;
          margin-bottom: 20px;
          font-size: 11.5px;
        }

        .lh-footer-container {
          background: #0d1e3d;
          width: 100%;
          border-radius: 2px;
          overflow: hidden;
          margin-top: 5px;
          font-family: Arial, Helvetica, sans-serif;
        }
        .lh-footer-top-curve {
          background: #103f7a;
          padding: 8px 15px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 10px;
        }
        .lh-footer-address {
          font-size: 8.5px;
          font-weight: bold;
          color: #ffffff;
          letter-spacing: 0.3px;
        }
        .lh-footer-email-box {
          background: #1c7c34;
          padding: 3px 12px;
          border-radius: 12px;
          font-size: 8.5px;
          font-weight: bold;
          color: #ffffff;
        }
        .lh-footer-logos-pane {
          background: #ffffff;
          padding: 5px 15px;
          display: flex;
          justify-content: flex-end;
          gap: 15px;
          align-items: center;
        }
        .lh-partner-logo { height: 16px; }
        .lh-footer-hindi-bar {
          background: #051429;
          padding: 6px 15px;
          text-align: center;
        }
        .lh-hindi-grid {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 3px 12px;
        }
        .lh-hindi-item {
          font-size: 8px;
          color: #cbd5e1;
        }
        .powered-by-tag {
          font-size: 6px;
          color: #4b5563;
          text-align: center;
          padding: 3px;
          background: #020b17;
          font-weight: bold;
          letter-spacing: 0.5px;
        }
      </style>
    </head>
    <body>
      <div class="print-master-wrapper">
        <div class="top-accent-bar"></div>
        
        <div class="lh-header-main">
          <div class="lh-logo-container">
            <img class="lh-logo-img" src="${logoSrc}" alt="Logo" />
          </div>
          <div class="lh-brand-section">
            <div class="lh-main-title">D.K. TRANSPORT CONSULTANCY</div>
            <div class="lh-sub-address">PARKING NO. 1 BABLA FUELS KE BAJU T P NAGAR RAWABHATA RAIPUR</div>
            <div class="lh-italic-tag">(Deal in all RTO related matters, Insurance - 2 Wheeler, 4 Wheeler, Commercial Vehicles)</div>
          </div>
          <div class="lh-contacts">
            <div class="lh-owner-name">Durgesh Kumar Siware</div>
            <div style="margin-bottom: 2px;">📞 79749 97280</div>
            <div>📞 73890 42205</div>
          </div>
        </div>

        <div class="lh-headline-label">VEHICLE DOCUMENT REPORT</div>

        <div class="bill-info-bar">
          <div class="info-group">
            <div><span class="info-label">Report No:</span><span class="info-value">DK/VEH/${new Date().getFullYear()}/${item.id || Math.floor(Math.random() * 10000)}</span></div>
            <div><span class="info-label">Generated On:</span><span class="info-value">${formattedDate} at ${formattedTime}</span></div>
          </div>
          <div><span class="info-label">Print Date:</span><span class="info-value">${formattedDate}</span></div>
        </div>

        <div class="details-grid">
          <table class="grid-table">
            <tr>
              <td style="padding: 4px 0; width: 50%;">
                <span class="grid-label">🚗 Vehicle Number:</span>
                <span class="grid-value">${item.vehicleNo}</span>
              </td>
              <td style="padding: 4px 0; width: 50%;">
                <span class="grid-label">👤 Owner Name:</span>
                <span class="grid-value">${item.party_name || item.ownerName || '—'}</span>
              </td>
            </tr>
            <tr>
              <td style="padding: 6px 0 4px 0; width: 50%;">
                <span class="grid-label">🔢 Chassis Number:</span>
                <span class="grid-value">${item.chassisNumber || '—'}</span>
              </td>
              <td style="padding: 6px 0 4px 0; width: 50%;">
                <span class="grid-label">📞 Mobile Number:</span>
                <span class="grid-value">${item.mobile_no || '—'}</span>
              </td>
            </tr>
          </table>
        </div>

        <div class="content-area-cell">
          <div class="watermark-wrapper">
            <img class="watermark-img" src="${logoSrc}" alt="Watermark" />
          </div>

          <table class="items-table">
            <thead>
              <tr>
                <th class="center" style="width: 10%;">S.No</th>
                <th style="width: 40%;">DOCUMENT TYPE</th>
                <th class="center" style="width: 25%;">VALIDITY DATE</th>
                <th class="center" style="width: 25%;">STATUS</th>
              </tr>
            </thead>
            <tbody>
              <tr><td class="center">1</td><td><strong>📄 Tax Validity</strong></td><td class="center">${item.taxValidity || '—'}</td><td class="center">${getStatusBadge(item.taxValidity)}</td></tr>
              <tr><td class="center">2</td><td><strong>💪 Fitness Validity</strong></td><td class="center">${item.fitnessValidity || '—'}</td><td class="center">${getStatusBadge(item.fitnessValidity)}</td></tr>
              <tr><td class="center">3</td><td><strong>🛡️ Insurance Validity</strong></td><td class="center">${item.insuranceValidity || '—'}</td><td class="center">${getStatusBadge(item.insuranceValidity)}</td></tr>
              <tr><td class="center">4</td><td><strong>🌿 Pollution Validity</strong></td><td class="center">${item.pollutionValidity || '—'}</td><td class="center">${getStatusBadge(item.pollutionValidity)}</td></tr>
              <tr>
  <td class="center">5</td>
  <td><strong>🚛 NP Validity</strong></td>
  <td class="center">${item.npValidity || '—'}</td>
  <td class="center">${getStatusBadge(item.npValidity)}</td>
</tr>

<tr>
  <td class="center">6</td>
  <td><strong>📋 CGP Validity</strong></td>
  <td class="center">${item.cgpValidity || '—'}</td>
  <td class="center">${getStatusBadge(item.cgpValidity)}</td>
</tr>
            </tbody>
          </table>

          <div class="bank-details">
            <div><strong>🏦 A/C NO:</strong> <span>38473468220</span></div>
            <div><strong>🏛️ IFSC:</strong> <span>SBIN0002852</span></div>
            <div><strong>👤 NAME:</strong> <span style="text-transform: uppercase;">Durgesh Kumar Siware</span></div>
            <div><strong>📞 MO:</strong> 7974997280, 7389042205</div>
          </div>

          <div class="signature-section">
            <div class="signature-line">
              <div class="signature-box"></div>
              <div style="color: #4b5563;">Customer Signature</div>
            </div>
            <div class="signature-line">
              <div class="auth-title">Durgesh Siware</div>
              <div style="border-top: 1px solid #cbd5e1; padding-top: 5px; color: #4b5563;">Authorized Signature</div>
            </div>
          </div>
        </div>

        <div class="lh-footer-container">
          <div class="lh-footer-top-curve">
            <div class="lh-footer-address">📍 Near Babla Fuels, Piyush Seat Cover Road, Parking No. 1, Transport Nagar, Raipur (C.G.) 493221</div>
            <div class="lh-footer-email-box">✉ durgeshshivare@gmail.com</div>
          </div>
          <div class="lh-footer-logos-pane">
            <svg class="lh-partner-logo" viewBox="0 0 100 40" xmlns="http://www.w3.org/2000/svg"><rect width="100" height="40" rx="3" fill="#0f4c81"/><circle cx="20" cy="20" r="10" fill="white" opacity="0.2"/><text x="60" y="21" font-family="sans-serif" font-weight="bold" font-size="7" fill="white" text-anchor="middle">UNITED INDIA</text><text x="60" y="30" font-family="sans-serif" font-size="5.5" fill="#cbd5e1" text-anchor="middle">INSURANCE</text></svg>
            <svg class="lh-partner-logo" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"><circle cx="20" cy="20" r="17" fill="#103f7a" stroke="#7ac143" stroke-width="1.5"/><path d="M15,12 L18,10 L22,13 L25,11 L28,16 L24,22 L26,28 L20,32 L16,28 L13,24 L16,18 Z" fill="white" opacity="0.85"/></svg>
          </div>
          <div class="lh-footer-hindi-bar">
            <div class="lh-hindi-grid">
              <span class="lh-hindi-item">ड्राइविंग लाइसेंस</span>
              <span class="lh-hindi-item">इंश्योरेंस</span>
              <span class="lh-hindi-item">रोड टैक्स</span>
              <span class="lh-hindi-item">परमिट</span>
              <span class="lh-hindi-item">नाम ट्रांसफर</span>
              <span class="lh-hindi-item">HSRP नं. प्लेट</span>
              <span class="lh-hindi-item">फिटनेस</span>
              <span class="lh-hindi-item">आर.टी.ओ वर्क</span>
            </div>
          </div>
          <div class="powered-by-tag">powered by yashvitech IT solution</div>
        </div>
      </div>
    </body>
    </html>
  `;

    const element = document.createElement('div');
    element.innerHTML = htmlContent;
    const opt = {
      margin: [0.3, 0.3, 0.3, 0.3],
      filename: `Vehicle_Report_${item.vehicleNo}_${new Date().toISOString().slice(0, 10)}.pdf`,
      image: { type: 'jpeg', quality: 0.95 },
      html2canvas: { scale: 2, useCORS: true, logging: false, letterRendering: true },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
    };
    html2pdf().set(opt).from(element).save().then(() => {
      message.success('PDF downloaded successfully!');
    }).catch((error) => {
      console.error('PDF generation error:', error);
      message.error('Failed to generate PDF');
    });
  };
  const exportToExcel = () => {
    setExportLoading(true);
    try {
      if (data.length === 0) { message.warning("No data to export!"); return; }
      const excelData = [["S.No", "Vehicle No", "Owner Name", "Chassis No", "Mobile No", "Tax Validity", "Fitness Validity", "Insurance Validity", "Pollution Validity", "Status"]];
      data.forEach((item, index) => {
        const hasExpired = isExpired(item.taxValidity) || isExpired(item.fitnessValidity) || isExpired(item.insuranceValidity) || isExpired(item.pollutionValidity);
        excelData.push([
          index + 1, item.vehicleNo, item.party_name || item.ownerName || '—',
          item.chassisNumber || '—', item.mobile_no || '—',
          item.taxValidity || '—', item.fitnessValidity || '—',
          item.insuranceValidity || '—', item.pollutionValidity || '—',
          hasExpired ? 'EXPIRED' : 'VALID'
        ]);
      });
      const ws = XLSX.utils.aoa_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "VehicleReport");
      XLSX.writeFile(wb, `Vehicle_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
      message.success("Export successful!");
    } catch (err) { message.error("Export failed!"); }
    finally { setExportLoading(false); }
  };

  const fetchRecords = async (page = pagination.current, pageSize = pagination.pageSize) => {
    setLoading(true);

    try {
      // Current page data
      const response = await getVehicleInsurances(filters, page, pageSize);

      // ALL records for cards
      const allResponse = await getVehicleInsurances(
        filters,
        1,
        response.count || 1000
      );

      if (response && response.results) {
        setData(response.results);

        setPagination({
          current: page,
          pageSize,
          total: response.count || 0
        });

        const stats = {
          taxExpiring: 0,
          fitnessExpiring: 0,
          insuranceExpiring: 0,
          pollutionExpiring: 0
        };

        allResponse.results.forEach(vehicle => {
          if (isExpiringSoon(vehicle.taxValidity))
            stats.taxExpiring++;

          if (isExpiringSoon(vehicle.fitnessValidity))
            stats.fitnessExpiring++;

          if (isExpiringSoon(vehicle.insuranceValidity))
            stats.insuranceExpiring++;

          if (isExpiringSoon(vehicle.pollutionValidity))
            stats.pollutionExpiring++;
        });

        setExpiryStats(stats);

        if (response.results.length === 0) {
          message.info("No records found");
        }
      }
    } catch (error) {
      console.error(error);
      message.error("Failed to fetch data");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (vehicleNo, id) => {
    Modal.confirm({
      title: 'Confirm Delete',
      content: `Are you sure you want to delete vehicle ${vehicleNo}?`,
      okText: 'Yes', okType: 'danger', cancelText: 'No',
      onOk: async () => {
        setLoading(true);
        try {
          await deleteVehicleInsurance(id);
          message.success(`${vehicleNo} deleted successfully`);
          await fetchRecords(1, pagination.pageSize);
        } catch (error) { message.error("Delete failed"); }
        finally { setLoading(false); }
      }
    });
  };

  const handleSearch = () => fetchRecords(1, pagination.pageSize);
  const handleClear = () => {
    setFilters({ vehicleNo: '', ownerName: '', expiryFilter: '' });
    setTimeout(() => fetchRecords(1, pagination.pageSize), 100);
  };
  const handleInputChange = (name, value) => setFilters(prev => ({ ...prev, [name]: value }));
  const handleTableChange = (newPagination) => fetchRecords(newPagination.current, newPagination.pageSize);

  useEffect(() => { fetchRecords(1, 10); }, []);

  // Desktop columns (exactly same as original)
  const columns = [
    { title: 'SR NO.', key: 'srno', width: 70, fixed: 'left', render: (_, __, i) => (pagination.current - 1) * pagination.pageSize + i + 1 },
    { title: 'Vehicle No', dataIndex: 'vehicleNo', key: 'vehicleNo', width: 130, fixed: 'left', render: (text) => <Tag color="blue">{text}</Tag> },
    { title: 'Owner Name', key: 'ownerName', width: 160, render: (_, record) => record.party_name || record.ownerName || '—' },
    { title: 'Chassis No', dataIndex: 'chassisNumber', key: 'chassisNumber', width: 150, render: (text) => text || '—' },
    {
      title: 'Tax Validity', key: 'taxValidity', width: 130, render: (_, record) => (
        <div><div>{record.taxValidity || '—'}</div>{record.taxValidity && <Tag color={getStatusColor(record.taxValidity)} style={{ marginTop: 4, fontSize: 10 }}>{getStatusText(record.taxValidity)}</Tag>}</div>
      )
    },
    {
      title: 'Fitness Validity', key: 'fitnessValidity', width: 130, render: (_, record) => (
        <div><div>{record.fitnessValidity || '—'}</div>{record.fitnessValidity && <Tag color={getStatusColor(record.fitnessValidity)} style={{ marginTop: 4, fontSize: 10 }}>{getStatusText(record.fitnessValidity)}</Tag>}</div>
      )
    },
    {
      title: 'Insurance Validity', key: 'insuranceValidity', width: 130, render: (_, record) => (
        <div><div>{record.insuranceValidity || '—'}</div>{record.insuranceValidity && <Tag color={getStatusColor(record.insuranceValidity)} style={{ marginTop: 4, fontSize: 10 }}>{getStatusText(record.insuranceValidity)}</Tag>}</div>
      )
    },
    {
      title: 'Pollution Validity', key: 'pollutionValidity', width: 130, render: (_, record) => (
        <div><div>{record.pollutionValidity || '—'}</div>{record.pollutionValidity && <Tag color={getStatusColor(record.pollutionValidity)} style={{ marginTop: 4, fontSize: 10 }}>{getStatusText(record.pollutionValidity)}</Tag>}</div>
      )
    },

    {
      title: 'NP Validity',
      key: 'npValidity',
      width: 130,
      render: (_, record) => (
        <div>
          <div>{record.npValidity || '—'}</div>
          {record.npValidity && (
            <Tag
              color={getStatusColor(record.npValidity)}
              style={{ marginTop: 4, fontSize: 10 }}
            >
              {getStatusText(record.npValidity)}
            </Tag>
          )}
        </div>
      )
    },
    {
      title: 'CGP Validity',
      key: 'cgpValidity',
      width: 130,
      render: (_, record) => (
        <div>
          <div>{record.cgpValidity || '—'}</div>
          {record.cgpValidity && (
            <Tag
              color={getStatusColor(record.cgpValidity)}
              style={{ marginTop: 4, fontSize: 10 }}
            >
              {getStatusText(record.cgpValidity)}
            </Tag>
          )}
        </div>
      )
    },
    {
      title: 'Documents', key: 'documents', width: 100, render: (_, record) => (
        <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => { setSelectedVehicle(record); setIsModalOpen(true); }}>View</Button>
      )
    },
    {
      title: 'Actions', key: 'actions', width: 120, fixed: 'right', render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit"><Button type="link" size="small" icon={<EditOutlined />} onClick={() => onEdit(record.id)} /></Tooltip>
          <Tooltip title="Delete"><Button type="link" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(record.vehicleNo, record.id)} /></Tooltip>
          <Tooltip title="Print"><Button type="link" size="small" icon={<PrinterOutlined />} onClick={() => handleSinglePrint(record)} /></Tooltip>
        </Space>
      )
    }
  ];

  // Mobile card: only show Vehicle No and Chassis No, rest inside expand/collapse
  const renderMobileCard = (record, index) => {
    const isExpanded = expandedKeys.includes(record.id);
    const srNo = (pagination.current - 1) * pagination.pageSize + index + 1;
    return (
      <Card key={record.id} style={{ marginBottom: 16, borderRadius: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <Tag color="blue">{record.vehicleNo}</Tag>
            <div style={{ marginTop: 8 }}>
              <Text strong>Owner:</Text> {record.party_name || record.ownerName || '—'}
              <div><Text type="secondary">Chassis: {record.chassisNumber || '—'}</Text></div>
            </div>
          </div>
          <Button type="link" icon={isExpanded ? <UpOutlined /> : <DownOutlined />} onClick={() => {
            if (isExpanded) setExpandedKeys(expandedKeys.filter(k => k !== record.id));
            else setExpandedKeys([...expandedKeys, record.id]);
          }} />
        </div>
        {isExpanded && (
          <div style={{ marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
            <div><strong>Tax:</strong> {record.taxValidity || '—'} <Tag color={getStatusColor(record.taxValidity)}>{getStatusText(record.taxValidity)}</Tag></div>
            <div><strong>Fitness:</strong> {record.fitnessValidity || '—'} <Tag color={getStatusColor(record.fitnessValidity)}>{getStatusText(record.fitnessValidity)}</Tag></div>
            <div><strong>Insurance:</strong> {record.insuranceValidity || '—'} <Tag color={getStatusColor(record.insuranceValidity)}>{getStatusText(record.insuranceValidity)}</Tag></div>
            <div><strong>Pollution:</strong> {record.pollutionValidity || '—'} <Tag color={getStatusColor(record.pollutionValidity)}>{getStatusText(record.pollutionValidity)}</Tag></div>
            <div>
              <strong>NP:</strong> {record.npValidity || '—'}
            </div>

            <div>
              <strong>CGP:</strong> {record.cgpValidity || '—'}
            </div>
            <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <Button size="small" icon={<EyeOutlined />} onClick={() => { setSelectedVehicle(record); setIsModalOpen(true); }}>Documents</Button>
              <Space>
                <Tooltip title="Edit"><Button type="link" icon={<EditOutlined />} onClick={() => onEdit(record.id)} /></Tooltip>
                <Tooltip title="Delete"><Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.vehicleNo, record.id)} /></Tooltip>
                <Tooltip title="Print"><Button type="link" icon={<PrinterOutlined />} onClick={() => handleSinglePrint(record)} /></Tooltip>
              </Space>
            </div>
          </div>
        )}
      </Card>
    );
  };

  return (
    <div style={{ minHeight: '100vh', padding: '20px', background: '#f0f2f5' }}>
      {/* Expiry Statistics Cards - 2 per row on mobile */}
      <Row gutter={[12, 12]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card className="stat-card" style={{ borderRadius: 10, background: '#fff7e6', borderLeft: '4px solid #fa8c16' }}>
            <Statistic
              title={<Text strong style={{ color: '#fa8c16' }}>Tax Expiring Soon</Text>}
              value={expiryStats.taxExpiring}
              prefix={<WarningOutlined style={{ color: '#fa8c16' }} />}
              suffix="vehicles"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className="stat-card" style={{ borderRadius: 10, background: '#fff7e6', borderLeft: '4px solid #fa8c16' }}>
            <Statistic
              title={<Text strong style={{ color: '#fa8c16' }}>Fitness Expiring Soon</Text>}
              value={expiryStats.fitnessExpiring}
              prefix={<WarningOutlined style={{ color: '#fa8c16' }} />}
              suffix="vehicles"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className="stat-card" style={{ borderRadius: 10, background: '#fff7e6', borderLeft: '4px solid #fa8c16' }}>
            <Statistic
              title={<Text strong style={{ color: '#fa8c16' }}>Insurance Expiring Soon</Text>}
              value={expiryStats.insuranceExpiring}
              prefix={<WarningOutlined style={{ color: '#fa8c16' }} />}
              suffix="vehicles"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className="stat-card" style={{ borderRadius: 10, background: '#fff7e6', borderLeft: '4px solid #fa8c16' }}>
            <Statistic
              title={<Text strong style={{ color: '#fa8c16' }}>Pollution Expiring Soon</Text>}
              value={expiryStats.pollutionExpiring}
              prefix={<WarningOutlined style={{ color: '#fa8c16' }} />}
              suffix="vehicles"
            />
          </Card>
        </Col>
      </Row>

      {/* Alert for expiring documents */}
      {(expiryStats.taxExpiring > 0 || expiryStats.fitnessExpiring > 0 || expiryStats.insuranceExpiring > 0 || expiryStats.pollutionExpiring > 0) && (
        <Alert
          message="⚠️ Document Expiry Alert"
          description={`${expiryStats.taxExpiring + expiryStats.fitnessExpiring + expiryStats.insuranceExpiring + expiryStats.pollutionExpiring} vehicle documents are expiring within the next 30 days.`}
          type="warning"
          showIcon
          style={{ marginBottom: 24, borderRadius: 8 }}
          closable
        />
      )}

      {/* Filter Section - same as original, just responsive */}
      <Card style={{ marginBottom: 24, borderRadius: 12, position: 'sticky', top: '-40px', zIndex: 999 }} bodyStyle={{ padding: '20px' }}>
        <Row gutter={[16, 16]} align="bottom">
          <Col xs={24} sm={12} md={4}>
            <Input placeholder="Search by vehicle number" value={filters.vehicleNo} onChange={(e) => handleInputChange('vehicleNo', e.target.value)} allowClear onPressEnter={handleSearch} />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Input placeholder="Search by owner name" value={filters.ownerName} onChange={(e) => handleInputChange('ownerName', e.target.value)} allowClear onPressEnter={handleSearch} />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select placeholder="Filter by document expiry" value={filters.expiryFilter || undefined} onChange={(value) => handleInputChange('expiryFilter', value)} style={{ width: '100%' }} allowClear>
              <Option value="tax_expiring">📄 Tax Validity (Expiring in 30 days)</Option>
              <Option value="fitness_expiring">💪 Fitness Validity (Expiring in 30 days)</Option>
              <Option value="insurance_expiring">🛡️ Insurance Validity (Expiring in 30 days)</Option>
              <Option value="pollution_expiring">🌿 Pollution Validity (Expiring in 30 days)</Option>
              <Option value="all_expired">⚠️ All Expired Documents</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Space size="middle" wrap>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>Search</Button>
              <Button icon={<ReloadOutlined />} onClick={handleClear}>Clear</Button>
              <Button icon={<DownloadOutlined />} onClick={exportToExcel} loading={exportLoading}>Export</Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Data Display: Desktop Table or Mobile Cards */}
      {!isMobile ? (
        <Card style={{ borderRadius: 12, overflow: 'hidden' }} bodyStyle={{ padding: 0 }}>
          <Table
            columns={columns}
            dataSource={data}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} vehicles`,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: ['10', '20', '50', '100']
            }}
            onChange={handleTableChange}
            size="middle"
            bordered
            scroll={{ x: 1400 }}
          />
        </Card>
      ) : (
        <div>
          {loading ? (
            <Card>Loading...</Card>
          ) : data.length === 0 ? (
            <Card>No vehicles found</Card>
          ) : (
            <>
              {data.map((record, idx) => renderMobileCard(record, idx))}

              <div
                style={{
                  textAlign: "center",
                  marginTop: 20,
                  paddingBottom: 20
                }}
              >
                <Pagination
                  current={pagination.current}
                  pageSize={pagination.pageSize}
                  total={pagination.total}
                  showSizeChanger
                  pageSizeOptions={['10', '20', '50', '100']}
                  onChange={(page, pageSize) =>
                    fetchRecords(page, pageSize)
                  }
                />
              </div>
            </>
          )}
        </div>
      )}

      {/* Document Modal - same as original */}
      <Modal
        title={<Space><FileTextOutlined style={{ color: '#1890ff' }} /> Vehicle Documents - {selectedVehicle?.vehicleNo}</Space>}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        width={800}
        footer={[<Button key="close" type="primary" onClick={() => setIsModalOpen(false)}>Close</Button>]}
      >
        {selectedVehicle && (
          <div>
            <div style={{ marginBottom: 20, padding: 16, background: '#f0f2f5', borderRadius: 8 }}>
              <Row gutter={16}>
                <Col span={12}><Text strong><UserOutlined /> Owner:</Text> <Text>{selectedVehicle.party_name || selectedVehicle.ownerName || '—'}</Text></Col>
                <Col span={12}><Text strong><CarOutlined /> Chassis:</Text> <Text>{selectedVehicle.chassisNumber || '—'}</Text></Col>
                <Col span={12}><Text strong><CalendarOutlined /> Mobile:</Text> <Text>{selectedVehicle.mobile_no || '—'}</Text></Col>
              </Row>
            </div>
            <Row gutter={[16, 16]}>
              {[
                { label: 'RC Book', path: selectedVehicle.rc_book, icon: <FileTextOutlined /> },
                { label: 'Insurance', path: selectedVehicle.insurance_pdf, icon: <SafetyOutlined /> },
                { label: 'Pollution', path: selectedVehicle.pollution_pdf, icon: <EnvironmentOutlined /> },
                { label: 'Fitness', path: selectedVehicle.fitness_doc, icon: <CalendarOutlined /> },
                { label: 'Permit', path: selectedVehicle.permit_doc, icon: <FileTextOutlined /> }
              ].map((doc, idx) => (
                <Col xs={24} sm={12} md={8} key={idx}>
                  <Card size="small" hoverable style={{ textAlign: 'center', height: '100%' }}>
                    <div style={{ fontSize: 32, color: doc.path ? '#52c41a' : '#d9d9d9' }}>{doc.icon}</div>
                    <div style={{ margin: '12px 0', fontWeight: 500 }}><b>{doc.label}</b></div>
                    {doc.path ? (
                      <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        <Button size="small" onClick={() => handleViewDoc(doc.path)} block><EyeOutlined /> View</Button>
                        <Button size="small" type="primary" onClick={() => handleDownload(doc.path, `${doc.label}.pdf`)} block><DownloadOutlined /> Download</Button>
                      </Space>
                    ) : <Tag color="default">Not Uploaded</Tag>}
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default VehicleReport;